import { useMutation, useQueryClient } from "@tanstack/react-query";
import type { AuthUser } from "@aurelia/types";
import { apiFetch } from "../../lib/api-client";
import { useAuthStore } from "../../lib/stores/auth";

interface RegisterPayload {
  phone: string;
  password: string;
  fullName: string;
  email?: string;
}

interface LoginPayload {
  identifier: string;
  password: string;
}

export type LoginResponse =
  | { status: "success"; user: AuthUser }
  | { status: "pin_required"; challengeToken: string }
  | { status: "pin_setup_required"; challengeToken: string };

export function useRegister() {
  const setUser = useAuthStore((s) => s.setUser);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: RegisterPayload) =>
      apiFetch<{ user: AuthUser }>("/auth/register", { method: "POST", body: JSON.stringify(payload) }),
    onSuccess: (data) => {
      setUser(data.user);
      // Not invalidateQueries: a stale pre-login "/auth/me" request (fired
      // before the user was authenticated) can still be in flight and
      // resolve after this, clobbering the user we just set with null. We
      // already have the authoritative result, so write it directly.
      queryClient.setQueryData(["me"], data);
    }
  });
}

export function useLogin() {
  return useMutation({
    mutationFn: (payload: LoginPayload) =>
      apiFetch<LoginResponse>("/auth/login", { method: "POST", body: JSON.stringify(payload) })
  });
}

export function useGoogleAuth() {
  return useMutation({
    mutationFn: (idToken: string) =>
      apiFetch<LoginResponse>("/auth/google", { method: "POST", body: JSON.stringify({ idToken }) })
  });
}

function useCompleteAuthStep(path: string) {
  const setUser = useAuthStore((s) => s.setUser);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { challengeToken: string; pin: string }) =>
      apiFetch<{ status: "success"; user: AuthUser }>(path, {
        method: "POST",
        body: JSON.stringify(payload)
      }),
    onSuccess: (data) => {
      setUser(data.user);
      // See useRegister's onSuccess for why this is setQueryData, not invalidateQueries.
      queryClient.setQueryData(["me"], data);
    }
  });
}

export function useVerifyPin() {
  return useCompleteAuthStep("/auth/verify-pin");
}

export function useSetupPin() {
  return useCompleteAuthStep("/auth/setup-pin");
}

export function useChangePassword() {
  return useMutation({
    mutationFn: (payload: { currentPassword: string; newPassword: string }) =>
      apiFetch<void>("/auth/account/password", { method: "PATCH", body: JSON.stringify(payload) })
  });
}

export function useChangePin() {
  return useMutation({
    mutationFn: (payload: { currentPassword: string; newPin: string }) =>
      apiFetch<void>("/auth/account/pin", { method: "PATCH", body: JSON.stringify(payload) })
  });
}

export function useChangeEmail() {
  const setUser = useAuthStore((s) => s.setUser);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload: { currentPassword: string; newEmail: string }) =>
      apiFetch<{ user: AuthUser }>("/auth/account/email", { method: "PATCH", body: JSON.stringify(payload) }),
    onSuccess: (data) => {
      setUser(data.user);
      // See useRegister's onSuccess for why this is setQueryData, not invalidateQueries.
      queryClient.setQueryData(["me"], data);
    }
  });
}

export function useLogout() {
  const setUser = useAuthStore((s) => s.setUser);
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<void>("/auth/logout", { method: "POST" }),
    onSuccess: () => {
      setUser(null);
      queryClient.clear();
    }
  });
}
